/**
 * Deal-screening workflow API (DOP-494..499).
 *
 * The only place that talks to `/api/deal-screening`. Every response is parsed
 * at the boundary with the schemas in `types/dealScreening.ts`; a malformed
 * payload throws `DealScreeningMalformedResponseError` so a page shows an error
 * state instead of rendering an unknown shape. A 409 (review refused while a
 * blocking gap is open, a run still generating) throws
 * `DealScreeningConflictError` carrying the server's detail, which the page
 * shows verbatim. Every other non-2xx — including the 422 a derived fact
 * answers a manual entry with — throws `DealScreeningRequestError`.
 */

import {
	type DealScreeningCreateDealPayload,
	type DealScreeningCreateWorkflowPayload,
	type DealScreeningDeal,
	type DealScreeningFolderCandidate,
	type DealScreeningMemoDraft,
	type DealScreeningClearFactPayload,
	type DealScreeningSaveVersionPayload,
	type DealScreeningSectionEditPayload,
	type DealScreeningResolveConflictPayload,
	type DealScreeningReviewPayload,
	type DealScreeningRun,
	type DealScreeningRunRequest,
	type DealScreeningRunSummary,
	type DealScreeningRunVersion,
	type DealScreeningSetFactPayload,
	type DealScreeningUpdateDealPayload,
	type DealScreeningUpdateWorkflowPayload,
	type DealScreeningWorkflow,
	type DealScreeningWorkflowListItem,
	parseDealScreeningDeal,
	parseDealScreeningDealList,
	parseDealScreeningFolderCandidateList,
	parseDealScreeningMemoDraft,
	parseDealScreeningRun,
	parseDealScreeningRunRequest,
	parseDealScreeningRunSummaryList,
	parseDealScreeningRunVersionList,
	parseDealScreeningWorkflow,
	parseDealScreeningWorkflowList,
} from "../types/dealScreening";
import { apiFetch } from "../utils/apiClient";
import { getApiBaseURLFromEnv } from "../utils/env";
import { saveExportFile } from "./monitoringService";

const BASE = "/api/deal-screening";
const HTTP_CONFLICT = 409;

export class DealScreeningConflictError extends Error {
	constructor(detail: string) {
		super(detail);
		this.name = "DealScreeningConflictError";
	}
}

export class DealScreeningRequestError extends Error {
	readonly status: number;
	constructor(status: number, detail: string) {
		super(detail);
		this.name = "DealScreeningRequestError";
		this.status = status;
	}
}

async function detailOf(response: Response): Promise<string> {
	try {
		const body = await response.json();
		if (body && typeof body.detail === "string") return body.detail;
		if (body && body.detail) return JSON.stringify(body.detail);
	} catch {
		// no JSON body
	}
	return response.statusText || `HTTP ${response.status}`;
}

async function request(
	endpoint: string,
	options: RequestInit = {},
): Promise<Response> {
	const response = await apiFetch(endpoint, options);
	if (response.ok) return response;
	const detail = await detailOf(response);
	if (response.status === HTTP_CONFLICT) {
		throw new DealScreeningConflictError(detail);
	}
	throw new DealScreeningRequestError(response.status, detail);
}

async function requestJson(
	endpoint: string,
	options: RequestInit = {},
): Promise<unknown> {
	const response = await request(endpoint, options);
	return response.json();
}

function jsonBody(payload: unknown, method = "POST"): RequestInit {
	return {
		method,
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	};
}

const id = encodeURIComponent;

// ----------------------------------------------------------------- workflows

export async function listWorkflows(): Promise<DealScreeningWorkflowListItem[]> {
	return parseDealScreeningWorkflowList(await requestJson(`${BASE}/workflows`));
}

export async function createWorkflow(
	payload: DealScreeningCreateWorkflowPayload,
): Promise<DealScreeningWorkflow> {
	return parseDealScreeningWorkflow(
		await requestJson(`${BASE}/workflows`, jsonBody(payload)),
	);
}

export async function getWorkflow(
	workflowId: string,
): Promise<DealScreeningWorkflow> {
	return parseDealScreeningWorkflow(
		await requestJson(`${BASE}/workflows/${id(workflowId)}`),
	);
}

/**
 * Rename, or edit the recipe through `recipe_edits` (merged server-side by
 * `spec.merge_recipe_edits`, which bumps the recipe version). The response is
 * the whole stored workflow; callers keep it in place of their own edit.
 */
export async function updateWorkflow(
	workflowId: string,
	patch: DealScreeningUpdateWorkflowPayload,
): Promise<DealScreeningWorkflow> {
	return parseDealScreeningWorkflow(
		await requestJson(
			`${BASE}/workflows/${id(workflowId)}`,
			jsonBody(patch, "PATCH"),
		),
	);
}

/** The workflow, its deals and every memo version they produced. */
export async function deleteWorkflow(workflowId: string): Promise<void> {
	await request(`${BASE}/workflows/${id(workflowId)}`, { method: "DELETE" });
}

// ----------------------------------------------------------------- deals

export async function listDeals(workflowId: string): Promise<DealScreeningDeal[]> {
	return parseDealScreeningDealList(
		await requestJson(`${BASE}/workflows/${id(workflowId)}/deals`),
	);
}

/** The owner's Deal Room folders a deal can point at. */
export async function listFolderCandidates(
	workflowId: string,
): Promise<DealScreeningFolderCandidate[]> {
	return parseDealScreeningFolderCandidateList(
		await requestJson(`${BASE}/workflows/${id(workflowId)}/folder-candidates`),
	);
}

export async function createDeal(
	workflowId: string,
	payload: DealScreeningCreateDealPayload,
): Promise<DealScreeningDeal> {
	return parseDealScreeningDeal(
		await requestJson(
			`${BASE}/workflows/${id(workflowId)}/deals`,
			jsonBody(payload),
		),
	);
}

export async function updateDeal(
	dealId: string,
	patch: DealScreeningUpdateDealPayload,
): Promise<DealScreeningDeal> {
	return parseDealScreeningDeal(
		await requestJson(`${BASE}/deals/${id(dealId)}`, jsonBody(patch, "PATCH")),
	);
}

export async function deleteDeal(dealId: string): Promise<void> {
	await request(`${BASE}/deals/${id(dealId)}`, { method: "DELETE" });
}

/** A new memo version for the deal — or the one already being generated. */
export async function runDeal(dealId: string): Promise<DealScreeningRunRequest> {
	return parseDealScreeningRunRequest(
		await requestJson(`${BASE}/deals/${id(dealId)}/runs`, { method: "POST" }),
	);
}

/** Stops a memo that is still being written; answers with the stopped run. */
export async function stopRun(runId: string): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(`${BASE}/runs/${id(runId)}/stop`, { method: "POST" }),
	);
}

export async function listDealRuns(
	dealId: string,
): Promise<DealScreeningRunSummary[]> {
	return parseDealScreeningRunSummaryList(
		await requestJson(`${BASE}/deals/${id(dealId)}/runs`),
	);
}

// ----------------------------------------------------------------- runs

export async function getRun(runId: string): Promise<DealScreeningRun> {
	return parseDealScreeningRun(await requestJson(`${BASE}/runs/${id(runId)}`));
}

export async function listRunVersions(
	runId: string,
): Promise<DealScreeningRunVersion[]> {
	return parseDealScreeningRunVersionList(
		await requestJson(`${BASE}/runs/${id(runId)}/versions`),
	);
}

/**
 * A person's value for a fact, or acceptance that it is not available. The
 * server recomputes derived figures, gaps and questions and answers with the
 * whole run; a derived fact answers 422 (`DealScreeningRequestError`).
 */
export async function setFact(
	runId: string,
	factKey: string,
	payload: DealScreeningSetFactPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(
			`${BASE}/runs/${id(runId)}/facts/${id(factKey)}`,
			jsonBody(payload, "PATCH"),
		),
	);
}

/** Choose which source a conflicted fact shows, or keep both flagged. */
export async function resolveConflict(
	runId: string,
	factKey: string,
	payload: DealScreeningResolveConflictPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(
			`${BASE}/runs/${id(runId)}/facts/${id(factKey)}/resolve`,
			jsonBody(payload),
		),
	);
}

/** Back to the system's value: the entry, acceptance or choice is superseded (DOP-577). */
export async function clearFact(
	runId: string,
	factKey: string,
	payload: DealScreeningClearFactPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(
			`${BASE}/runs/${id(runId)}/facts/${id(factKey)}/clear`,
			jsonBody(payload),
		),
	);
}

/** A person's edit to one section as an edit script; 409 when the generated text changed (DOP-580). */
/** Saves edits across the memo as a new version and answers with it (DOP-621). */
export async function saveVersion(
	runId: string,
	payload: DealScreeningSaveVersionPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(`${BASE}/runs/${id(runId)}/versions`, jsonBody(payload, "POST")),
	);
}

export async function editSection(
	runId: string,
	sectionKey: string,
	payload: DealScreeningSectionEditPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(
			`${BASE}/runs/${id(runId)}/sections/${id(sectionKey)}/edit`,
			jsonBody(payload, "PUT"),
		),
	);
}

/** Back to the generated text; the edit stays as history (DOP-580). */
export async function clearSectionEdit(
	runId: string,
	sectionKey: string,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(`${BASE}/runs/${id(runId)}/sections/${id(sectionKey)}/edit`, {
			method: "DELETE",
		}),
	);
}

/** Make an earlier decision the active one again (DOP-577). */
export async function revertDecision(
	runId: string,
	decisionId: string,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(
			`${BASE}/runs/${id(runId)}/decisions/${decisionId}/revert`,
			jsonBody({}),
		),
	);
}

/** 409 (`DealScreeningConflictError`) while a blocking gap is open. */
export async function reviewRun(
	runId: string,
	payload: DealScreeningReviewPayload = {},
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(`${BASE}/runs/${id(runId)}/review`, jsonBody(payload)),
	);
}

// ----------------------------------------------------------------- export

// ----------------------------------------------------------------- shared draft (memo co-editing)

/** The deal's shared draft, or null when nobody has opened one yet. */
export async function getMemoDraft(dealId: string): Promise<DealScreeningMemoDraft | null> {
	try {
		return parseDealScreeningMemoDraft(await requestJson(`${BASE}/deals/${id(dealId)}/memo-draft`));
	} catch (caught) {
		if (caught instanceof DealScreeningRequestError && caught.status === 404) return null;
		throw caught;
	}
}

/** Start the shared draft from a version; the first seed wins and a later
 * one gets the draft that already exists. `update` is the Yjs document, base64. */
export async function seedMemoDraft(
	dealId: string,
	baseRunId: string,
	update: string,
): Promise<DealScreeningMemoDraft> {
	return parseDealScreeningMemoDraft(
		await requestJson(
			`${BASE}/deals/${id(dealId)}/memo-draft`,
			jsonBody({ base_run_id: baseRunId, update }),
		),
	);
}

/** Throw the shared draft away: everyone in it is cut off and starts again. */
export async function discardMemoDraft(dealId: string): Promise<void> {
	await request(`${BASE}/deals/${id(dealId)}/memo-draft`, { method: "DELETE" });
}

/** "Save as version N": the shared draft becomes the deal's next version. */
export async function saveMemoDraftVersion(
	dealId: string,
	payload: DealScreeningSaveVersionPayload,
): Promise<DealScreeningRun> {
	return parseDealScreeningRun(
		await requestJson(`${BASE}/deals/${id(dealId)}/memo-draft/versions`, jsonBody(payload)),
	);
}

/** The socket subprotocol the server answers with, and the one carrying the token. */
export const MEMO_SOCKET_PROTOCOL = "doppler.memo";
export const MEMO_SOCKET_TOKEN_PREFIX = "doppler.token.";

/**
 * Where the deal's shared draft syncs: the provider joins `${url}/${room}`
 * (`.../memo-draft/ws`) offering `protocols`, the second of which carries the
 * session's token — in a header, never in the URL.
 */
export function memoDraftSocket(dealId: string): { url: string; room: string; protocols: string[] } {
	const base = (getApiBaseURLFromEnv() ?? window.location.origin).replace(/\/$/, "");
	const url = new URL(`${base}${BASE}/deals/${id(dealId)}/memo-draft`);
	url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
	return {
		url: url.toString(),
		room: "ws",
		protocols: [MEMO_SOCKET_PROTOCOL, `${MEMO_SOCKET_TOKEN_PREFIX}${localStorage.getItem("access_token") ?? ""}`],
	};
}

export interface DealScreeningExportFile {
	blob: Blob;
	filename: string;
}

function filenameFrom(response: Response, fallback: string): string {
	const header = response.headers.get("Content-Disposition") ?? "";
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
	return match ? decodeURIComponent(match[1]) : fallback;
}

/**
 * The memo as Word, fetched through the API proxy as a blob so the browser
 * saves it under the server's filename (DOP-425: a cross-origin anchor ignores
 * the download attribute). Save it with `saveExportFile`.
 */
export async function exportRunDocx(runId: string): Promise<DealScreeningExportFile> {
	const response = await request(`${BASE}/runs/${id(runId)}/export.docx`);
	return {
		blob: await response.blob(),
		filename: filenameFrom(response, "opportunity_memo.docx"),
	};
}

/** The same memo as a PDF, drawn server-side from the same walk as the .docx. */
export async function exportRunPdf(runId: string): Promise<DealScreeningExportFile> {
	const response = await request(`${BASE}/runs/${id(runId)}/export.pdf`);
	return {
		blob: await response.blob(),
		filename: filenameFrom(response, "opportunity_memo.pdf"),
	};
}

export const dealScreeningService = {
	getMemoDraft,
	seedMemoDraft,
	discardMemoDraft,
	saveMemoDraftVersion,
	memoDraftSocket,
	listWorkflows,
	createWorkflow,
	getWorkflow,
	updateWorkflow,
	deleteWorkflow,
	listDeals,
	listFolderCandidates,
	createDeal,
	updateDeal,
	deleteDeal,
	runDeal,
	listDealRuns,
	getRun,
	listRunVersions,
	setFact,
	resolveConflict,
	clearFact,
	revertDecision,
	editSection,
	saveVersion,
	stopRun,
	clearSectionEdit,
	reviewRun,
	exportRunDocx,
	exportRunPdf,
	// One implementation of "save a blob without a cross-origin anchor" for
	// every workflow export; the file shape is the same `{blob, filename}`.
	saveExportFile,
};
